import { CopyToClipboardButton } from '@/components/Patient/components/CopyToClipboardButton';
import { FF_PATIENT_DETAILS_SKELETON_ENABLED } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import { useAppStore } from '@/state-management';
import { ListItemText } from '@montugroup/design-system';
import Flag from '@mui/icons-material/Flag';
import { Box, CircularProgress, List, ListItem, ListItemIcon, Skeleton, Tooltip, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React from 'react';

interface PatientBasicInfoProps {
  isLoading: boolean;
}

export const PatientBasicInfo: React.FC<PatientBasicInfoProps> = ({ isLoading }) => {
  const { patientCode, basicInfoData, redflagInfoData } = useAppStore((state) => ({
    patientCode: state.getPatientCode(),
    basicInfoData: state.getPatientBasicInfo(),
    redflagInfoData: state.getPatientRedFlagInfo(),
  }));

  const { flags } = useFeatureFlags();

  const isSkeletonEnabled = flags[FF_PATIENT_DETAILS_SKELETON_ENABLED];

  const patientDobFormatted = basicInfoData.dob ? DateTime.fromISO(basicInfoData.dob).toFormat('dd-MMM-yyyy') : '';

  if (isLoading || !basicInfoData.firstName) {
    return isSkeletonEnabled ? (
      <>
        <Skeleton variant="rounded" height={26} width={110} sx={{ mb: 1 }} />
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={16} width={110} sx={{ mt: 1, mb: 1 }} />
        ))}
      </>
    ) : (
      <Box justifyContent="center" alignItems="center" gap="1" display="flex">
        <CircularProgress size={25} />
        <Typography variant="h4">Loading</Typography>
      </Box>
    );
  }

  return (
    <Box data-testid={'patient-basic-info'}>
      <List dense>
        <ListItem disablePadding>
          <ListItemText
            primary={`${basicInfoData?.firstName} ${basicInfoData?.lastName}`}
            primaryTypographyProps={{ fontWeight: 600, variant: 'h6' }}
            data-dd-action-name="Patient's Fullname"
          />
          {redflagInfoData?.hasRedFlag && (
            <ListItemIcon>
              <Tooltip title={redflagInfoData?.redFlagComment}>
                <Flag color="error" />
              </Tooltip>
            </ListItemIcon>
          )}
        </ListItem>
        <ListItem disablePadding>
          <ListItemText
            data-dd-privacy="allow"
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                <span>{patientCode}</span>
                {patientCode && (
                  <CopyToClipboardButton text={patientCode} label="Copy ALPAT code" tooltipTitle="Copy ALPAT code" />
                )}
              </Box>
            }
            primaryTypographyProps={{ variant: 'body1' }}
          />
        </ListItem>
        <ListItem disablePadding>
          <ListItemText
            data-testid="patient-basic-info-dob"
            data-dd-action-name="Patient's Dob"
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                <span>
                  <strong>DoB:</strong> {basicInfoData?.dob ? patientDobFormatted : 'N/A'}
                </span>
                {basicInfoData?.dob && (
                  <CopyToClipboardButton
                    text={patientDobFormatted}
                    label="Copy date of birth"
                    tooltipTitle="Copy date of birth"
                  />
                )}
              </Box>
            }
            primaryTypographyProps={{ variant: 'body1' }}
          />
        </ListItem>

        <ListItem disablePadding>
          <ListItemText
            data-testid="patient-basic-info-age"
            primary={
              <>
                <strong>Age:</strong>{' '}
                {basicInfoData?.dob
                  ? Math.floor(DateTime.now().diff(DateTime.fromISO(basicInfoData.dob), ['years']).years)
                  : 'N/A'}
              </>
            }
            primaryTypographyProps={{ variant: 'body1' }}
          />
        </ListItem>
      </List>
    </Box>
  );
};
